import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import clsx from 'clsx';
import { Eraser, Undo2 } from 'lucide-react';

export interface SignaturePadHandle {
  /** PNG (transparenter Hintergrund, auf die Unterschrift zugeschnitten) oder null, wenn leer */
  toPng: () => string | null;
  clear: () => void;
  isEmpty: () => boolean;
}

interface Point {
  x: number;
  y: number;
  p: number;
}

/**
 * Unterschriftenfeld für Maus, Finger und Stift (Pointer Events).
 * Striche werden als Vektoren gehalten und bei Größenänderung neu gezeichnet; Stiftdruck steuert die Strichstärke.
 */
export const SignaturePad = forwardRef<SignaturePadHandle, { onChange?: (empty: boolean) => void; className?: string; label?: string }>(function SignaturePad(
  { onChange, className, label = 'Hier unterschreiben' },
  ref,
) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const strokes = useRef<Point[][]>([]);
  const current = useRef<Point[] | null>(null);
  const [empty, setEmpty] = useState(true);

  const lineWidth = (p: number) => 1.4 + 2.2 * (p > 0 ? p : 0.5);

  const redraw = useCallback(() => {
    const c = canvas.current;
    if (!c) return;
    const ctx = c.getContext('2d')!;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.strokeStyle = '#0f1f3a';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (const s of strokes.current) drawStroke(ctx, s);
  }, []);

  const drawStroke = (ctx: CanvasRenderingContext2D, s: Point[]) => {
    if (s.length === 1) {
      ctx.beginPath();
      ctx.arc(s[0]!.x, s[0]!.y, lineWidth(s[0]!.p) / 2, 0, Math.PI * 2);
      ctx.fillStyle = '#0f1f3a';
      ctx.fill();
      return;
    }
    for (let i = 1; i < s.length; i++) {
      const a = s[i - 1]!;
      const b = s[i]!;
      ctx.beginPath();
      ctx.lineWidth = lineWidth((a.p + b.p) / 2);
      ctx.moveTo(a.x, a.y);
      // Glättung über Mittelpunkte
      const n = s[i + 1];
      if (n) ctx.quadraticCurveTo(b.x, b.y, (b.x + n.x) / 2, (b.y + n.y) / 2);
      else ctx.lineTo(b.x, b.y);
      ctx.stroke();
    }
  };

  // Canvas an die Anzeigegröße (inkl. Retina) anpassen
  useEffect(() => {
    const c = canvas.current;
    if (!c) return;
    const resize = () => {
      const r = c.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      c.width = Math.round(r.width * dpr);
      c.height = Math.round(r.height * dpr);
      redraw();
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(c);
    return () => ro.disconnect();
  }, [redraw]);

  const pos = (e: ReactPointerEvent<HTMLCanvasElement>): Point => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top, p: e.pointerType === 'pen' ? e.pressure : 0.5 };
  };

  const update = (isEmpty: boolean) => {
    setEmpty(isEmpty);
    onChange?.(isEmpty);
  };

  const onDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    current.current = [pos(e)];
    strokes.current.push(current.current);
    redraw();
  };
  const onMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!current.current) return;
    e.preventDefault();
    const events = typeof e.nativeEvent.getCoalescedEvents === 'function' ? e.nativeEvent.getCoalescedEvents() : [e.nativeEvent];
    const r = e.currentTarget.getBoundingClientRect();
    for (const ev of events) current.current.push({ x: ev.clientX - r.left, y: ev.clientY - r.top, p: ev.pointerType === 'pen' ? ev.pressure : 0.5 });
    redraw();
  };
  const onUp = () => {
    if (!current.current) return;
    current.current = null;
    update(strokes.current.length === 0);
  };

  const clear = () => {
    strokes.current = [];
    redraw();
    update(true);
  };
  const undo = () => {
    strokes.current.pop();
    redraw();
    update(strokes.current.length === 0);
  };

  useImperativeHandle(ref, () => ({
    isEmpty: () => strokes.current.length === 0,
    clear,
    toPng: () => {
      const c = canvas.current;
      if (!c || strokes.current.length === 0) return null;
      // Auf die Unterschrift zuschneiden (mit Rand), in doppelter Auflösung exportieren
      const all = strokes.current.flat();
      const pad = 8;
      const minX = Math.max(0, Math.min(...all.map((p) => p.x)) - pad);
      const minY = Math.max(0, Math.min(...all.map((p) => p.y)) - pad);
      const maxX = Math.max(...all.map((p) => p.x)) + pad;
      const maxY = Math.max(...all.map((p) => p.y)) + pad;
      const scale = 2;
      const out = document.createElement('canvas');
      out.width = Math.max(1, Math.round((maxX - minX) * scale));
      out.height = Math.max(1, Math.round((maxY - minY) * scale));
      const ctx = out.getContext('2d')!;
      ctx.setTransform(scale, 0, 0, scale, -minX * scale, -minY * scale);
      ctx.strokeStyle = '#0f1f3a';
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      for (const s of strokes.current) drawStroke(ctx, s);
      return out.toDataURL('image/png');
    },
  }));

  return (
    <div className={clsx('select-none', className)}>
      <div className="relative overflow-hidden rounded-xl border-2 border-dashed border-slate-300 bg-white">
        <canvas
          ref={canvas}
          className="block h-48 w-full cursor-crosshair touch-none sm:h-56"
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          aria-label={label}
          role="img"
        />
        {/* Unterschriftslinie */}
        <div className="pointer-events-none absolute inset-x-6 bottom-10 border-b border-slate-300" />
        <span className="pointer-events-none absolute bottom-10 left-6 -translate-y-1 text-lg text-slate-300">✕</span>
        {empty && <span className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 text-center text-sm text-slate-400">{label}</span>}
      </div>
      <div className="mt-2 flex justify-end gap-2">
        <button type="button" onClick={undo} disabled={empty} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-slate-600 hover:bg-slate-100 disabled:opacity-40">
          <Undo2 className="h-4 w-4" /> Rückgängig
        </button>
        <button type="button" onClick={clear} disabled={empty} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-slate-600 hover:bg-slate-100 disabled:opacity-40">
          <Eraser className="h-4 w-4" /> Löschen
        </button>
      </div>
    </div>
  );
});
